import { useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SkeletonDetail } from '../components/common/SkeletonDetail';
import { AiCreditsTab } from '../components/settings/AiCreditsTab';
import { AssistantSettingsTab } from '../components/settings/AssistantSettingsTab';
import { EmailTemplatesTab } from '../components/settings/EmailTemplatesTab';
import { GeneralSettingsTab } from '../components/settings/GeneralSettingsTab';
import { PipelineDefaultsTab } from '../components/settings/PipelineDefaultsTab';
import { SendGridTab } from '../components/settings/SendGridTab';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import type { SystemSettingsTyped } from '../types';

type SettingsTab = 'general' | 'sendgrid' | 'pipeline-defaults' | 'email-templates' | 'assistant' | 'ai-credits';

interface SettingsTabDefinition {
  id: SettingsTab;
  label: string;
  icon: string;
  description: string;
  isSaveable: boolean;
}

const SETTINGS_TABS: SettingsTabDefinition[] = [
  { id: 'general', icon: '⚙️', label: 'General Settings', description: 'System-wide configuration', isSaveable: true },
  { id: 'sendgrid', icon: '📧', label: 'SendGrid', description: 'Email notification settings', isSaveable: true },
  { id: 'pipeline-defaults', icon: '🔧', label: 'Pipeline Defaults', description: 'Default pipeline step settings', isSaveable: false },
  { id: 'email-templates', icon: '📧', label: 'Email Templates', description: 'Manage notification templates', isSaveable: false },
  { id: 'assistant', icon: '🤖', label: 'AI Assistant', description: 'Beacon AI model configuration', isSaveable: true },
  { id: 'ai-credits', icon: '💰', label: 'AI Credits', description: 'AI credit pricing and model rates', isSaveable: false },
];

function getErrorMessage(error: unknown): string {
  const apiError = error as { response?: { data?: { title?: string; detail?: string; message?: string } }; message?: string };
  return apiError.response?.data?.detail
    ?? apiError.response?.data?.title
    ?? apiError.response?.data?.message
    ?? apiError.message
    ?? 'Failed to load settings';
}

interface SettingsPageContentProps {
  initialSettings: SystemSettingsTyped;
}

function SettingsPageContent({ initialSettings }: SettingsPageContentProps) {
  const appVersion = import.meta.env.VITE_APP_VERSION || '0.156.0';
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [settings, setSettings] = useState<SystemSettingsTyped>(initialSettings);
  const settingsSeed = JSON.stringify(initialSettings);
  const [settingsSeedSnapshot, setSettingsSeedSnapshot] = useState(settingsSeed);
  const isSettingsSeedChanging = settingsSeedSnapshot !== settingsSeed;
  if (isSettingsSeedChanging) {
    setSettingsSeedSnapshot(settingsSeed);
    setSettings(initialSettings);
  }
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const saveMutation = useMutation({
    mutationFn: () => apiClient.updateSystemSettings(settings),
    onSuccess: async (savedSettings) => {
      setSettings(savedSettings);
      setSaveError(null);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      queryClient.setQueryData(['system-settings'], savedSettings);
      await queryClient.invalidateQueries({ queryKey: ['system-settings'] });
    },
    onError: (error) => {
      setSaveSuccess(false);
      setSaveError(getErrorMessage(error));
    },
  });

  const activeTabIndex = SETTINGS_TABS.findIndex((tab) => tab.id === activeTab);
  const activeTabConfig = SETTINGS_TABS[activeTabIndex];

  const setFocusedTab = (targetIndex: number) => {
    const normalizedIndex = (targetIndex + SETTINGS_TABS.length) % SETTINGS_TABS.length;
    setActiveTab(SETTINGS_TABS[normalizedIndex].id);
    tabRefs.current[normalizedIndex]?.focus();
  };

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, tabIndex: number) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      setFocusedTab(tabIndex + 1);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setFocusedTab(tabIndex - 1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      setFocusedTab(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      setFocusedTab(SETTINGS_TABS.length - 1);
    }
  };

  const updateSettings = (nextValue: SystemSettingsTyped) => {
    setSettings(nextValue);
    setSaveSuccess(false);
    setSaveError(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">System Settings</h1>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">App version: {appVersion}</p>
        </div>
        <div className="flex items-center gap-2">
          {saveSuccess && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-2 dark:border-green-800 dark:bg-green-900/20">
              <p className="flex items-center text-sm text-green-800 dark:text-green-300">
                <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Settings saved successfully
              </p>
            </div>
          )}
          {saveError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 dark:border-red-800 dark:bg-red-900/20">
              <p className="text-sm text-red-800 dark:text-red-300">{saveError}</p>
            </div>
          )}
        </div>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex flex-wrap gap-x-6" aria-label="Settings tabs" role="tablist">
          {SETTINGS_TABS.map((tab, index) => {
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                ref={(element) => {
                  tabRefs.current[index] = element;
                }}
                type="button"
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                aria-controls={`settings-panel-${tab.id}`}
                id={`settings-tab-${tab.id}`}
                title={tab.description}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
                className={`inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  isActive
                    ? 'border-primary-500 text-primary-700 dark:text-primary-300'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:hover:border-gray-600 dark:hover:text-gray-300'
                }`}
              >
                <span className="text-lg" aria-hidden="true">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      <section
        id={`settings-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`settings-tab-${activeTab}`}
        className="mt-6"
      >
        {activeTab === 'general' && (
          <GeneralSettingsTab
            settings={settings.general}
            onChange={(general) => updateSettings({ ...settings, general })}
          />
        )}
        {activeTab === 'sendgrid' && (
          <SendGridTab
            settings={settings.sendGrid}
            onChange={(sendGrid) => updateSettings({ ...settings, sendGrid })}
            onManageEmailTemplates={() => setActiveTab('email-templates')}
          />
        )}
        {activeTab === 'pipeline-defaults' && <PipelineDefaultsTab />}
        {activeTab === 'email-templates' && <EmailTemplatesTab />}
        {activeTab === 'assistant' && (
          <AssistantSettingsTab
            value={settings.assistant}
            aiCredits={settings.aiCredits}
            onChange={(assistant) => updateSettings({ ...settings, assistant })}
            onAiCreditsChange={(aiCredits) => updateSettings({ ...settings, aiCredits })}
          />
        )}
        {activeTab === 'ai-credits' && <AiCreditsTab />}
      </section>

      {activeTabConfig.isSaveable && (
        <div className="flex justify-end border-t border-gray-200 pt-6 dark:border-gray-700">
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="rounded-md bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
            aria-busy={saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <span className="inline-flex items-center gap-2">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                Saving...
              </span>
            ) : (
              'Save Settings'
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export function SettingsPage() {
  const { user, permissions } = useAuth();
  const hasSystemAdminPermission = user?.role === 'SystemAdmin' || permissions.some((permission) => permission.toLowerCase() === 'systemadmin');

  const settingsQuery = useQuery({
    queryKey: ['system-settings'],
    queryFn: () => apiClient.getSystemSettings(),
    enabled: hasSystemAdminPermission,
  });

  if (!hasSystemAdminPermission) {
    return (
      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
        <h3 className="mb-2 font-semibold text-yellow-800 dark:text-yellow-300">Access Denied</h3>
        <p className="text-yellow-700 dark:text-yellow-400">You need SystemAdmin permission to access system settings.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {settingsQuery.isLoading && (
        <div className="rounded border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <SkeletonDetail />
        </div>
      )}

      {settingsQuery.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <p className="text-red-800 dark:text-red-300">{getErrorMessage(settingsQuery.error)}</p>
        </div>
      )}

      {settingsQuery.data && (
        <SettingsPageContent initialSettings={settingsQuery.data} />
      )}
    </div>
  );
}
